import type { PersistenceProvider } from '../persistence/localStorage';
import { GameSession } from '../session';
import { tryParseSave } from '../sim/save';
import type { ParkState } from '../types';

/** Once-only raw copy of the canonical save, taken before the first Three.js write. */
export const PRE_THREEJS_CUTOVER_BACKUP_KEY = 'park_v1_wu001_save_pre_threejs_cutover_backup_v1';

export type CanonicalSaveInspect =
  | { status: 'empty'; raw: null }
  | { status: 'valid'; raw: string; state: ParkState }
  | { status: 'incompatible'; raw: string };

export type CutoverOpenResult =
  | {
      ok: true;
      session: GameSession;
      inspect: Extract<CanonicalSaveInspect, { status: 'empty' | 'valid' }>;
      backupCreated: boolean;
      backupRaw: string | null;
    }
  | {
      ok: false;
      reason: 'incompatible_canonical_save';
      inspect: Extract<CanonicalSaveInspect, { status: 'incompatible' }>;
    };

export function inspectCanonicalSave(raw: string | null | undefined): CanonicalSaveInspect {
  if (raw == null || raw === '') {
    return { status: 'empty', raw: null };
  }
  const state = tryParseSave(raw);
  if (!state) {
    return { status: 'incompatible', raw };
  }
  return { status: 'valid', raw, state };
}

/**
 * Copy the exact canonical raw bytes once. Never overwrites an existing backup.
 * Not the active save.
 */
export function ensurePreCutoverBackup(
  backupPersistence: PersistenceProvider,
  raw: string,
): { created: boolean; raw: string } {
  const existing = backupPersistence.load();
  if (existing !== null) {
    return { created: false, raw: existing };
  }
  backupPersistence.save(raw);
  return { created: true, raw };
}

/**
 * Open the production Three.js session against the canonical save.
 * Backs up a valid raw save before the first GameSession write.
 * Never writes when the canonical payload is incompatible.
 */
export function openCanonicalThreeJsSession(
  persistence: PersistenceProvider,
  backupPersistence: PersistenceProvider,
  now = Date.now(),
): CutoverOpenResult {
  const inspect = inspectCanonicalSave(persistence.load());
  if (inspect.status === 'incompatible') {
    return { ok: false, reason: 'incompatible_canonical_save', inspect };
  }

  let backupCreated = false;
  let backupRaw: string | null = backupPersistence.load();
  if (inspect.status === 'valid') {
    const backup = ensurePreCutoverBackup(backupPersistence, inspect.raw);
    backupCreated = backup.created;
    backupRaw = backup.raw;
  }

  const session = new GameSession(persistence, now);
  return {
    ok: true,
    session,
    inspect,
    backupCreated,
    backupRaw,
  };
}
